import React from 'react';
import URI from 'urijs';
import { windowWrapper } from '@clubmed/utils';
import config from 'Config';
import AsyncScriptLoader from 'Components/utils/async-script-loader/AsyncScriptLoader';
import { API_VERSION, BAIDU_CALLBACK } from 'Components/utils/baidu/baidu.constants';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { useAppContext } from 'Context/ReactAppContext';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { Map } from './Map';
const SQUARE_MARKER = '/markers/squareMarker.png';
const MARKER = '/markers/marker.png';
export type Props = {
  className?: string;
  closeLabel?: string;
  height: number;
  latitude: number;
  loader: () => void;
  longitude: number;
  scale: number;
  title?: string;
  width: number;
  zoom: number;
};
export const BaiduMap = ({
  className,
  closeLabel,
  latitude,
  longitude,
  width = 780,
  height = 420,
  scale = 2,
  title,
  zoom = 7,
  loader = () => require('scriptjs'),
}: Props) => {
  const track = useTracking();
  const responsive = useResponsive();
  const appContext = useAppContext();
  const mapRef = React.useRef<HTMLDivElement | null | undefined>();
  const origin = new URI(appContext.originalUrl).origin();

  const getBaiduBaseUrl = () => {
    const apiConfig = config.get('baidumap.api');
    return new URI(apiConfig.domain).addQuery({
      ak: apiConfig.key,
      v: API_VERSION,
    });
  };

  const getMarkerUrl = (marker: string) => {
    return `${origin}${marker}`;
  };

  const initInteractiveMap = (isLarge: boolean) => {
    const BMap = windowWrapper.get('BMap');
    const centerPt = new BMap.Point(longitude, latitude);
    const markerPt = new BMap.Point(longitude, latitude);
    const icon = new BMap.Icon(getMarkerUrl(MARKER), new BMap.Size(58, 70), {
      anchor: new BMap.Size(29, 70),
      infoWindowAnchor: new BMap.Size(29, 0),
    });
    const marker = new BMap.Marker(markerPt, {
      icon,
    });
    const map = new BMap.Map(mapRef.current);

    // Workaround a BaiduMap bug for pinchzoom on mobile
    if (isLarge) {
      Object.defineProperty(mapRef.current, 'offsetParent', {
        get: () => windowWrapper.get('document.body'),
      });
    }

    map.enableScrollWheelZoom();
    map.centerAndZoom(centerPt, zoom + 1);
    map.addOverlay(marker);
  };

  const isBaiduMapLoaded = () => {
    const BMap = windowWrapper.get('BMap');
    return BMap && BMap.version;
  };

  const getStaticMapOptions = () => {
    return {
      width,
      height,
      scale,
      zoom,
      center: `${longitude},${latitude}`,
      markers: `${longitude},${latitude}`,
      markerStyles: `-1,${getMarkerUrl(SQUARE_MARKER)}`,
    };
  };

  const getStaticMapURL = () => {
    const baseUrl = getBaiduBaseUrl();
    baseUrl.path(config.get('baidumap.api.staticPath'));
    baseUrl.addQuery(getStaticMapOptions());
    return baseUrl.toString();
  };

  const getBaiduScriptUrl = () => {
    const baseUrl = getBaiduBaseUrl();
    baseUrl.path(config.get('baidumap.api.jsPath'));
    baseUrl.addQuery('callback', BAIDU_CALLBACK);
    return baseUrl.toString();
  };

  return (
    <Map
      className={className}
      closeLabel={closeLabel}
      imageSrc={getStaticMapURL()}
      isLarge={responsive.isTablet}
      onClick={() =>
        track({
          categoryGA: 'resort',
          actionGA: 'mapClick',
          labelGA: 'BaiduMap',
        })
      }
      ratio={{
        w: width,
        h: height,
      }}
      title={title}
    >
      <AsyncScriptLoader
        scriptURL={getBaiduScriptUrl()}
        loader={loader}
        preload={() => {
          windowWrapper.set(BAIDU_CALLBACK, () => {
            initInteractiveMap(responsive.isTablet);
          });
        }}
        callback={() => {
          isBaiduMapLoaded() ? windowWrapper.get(BAIDU_CALLBACK)() : () => {};
        }}
      >
        <div className="h-full" data-testid="BaiduMapContainer" ref={mapRef} />
      </AsyncScriptLoader>
    </Map>
  );
};
